
import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Sidebar, Menu, MenuItem, SubMenu, sidebarClasses } from "react-pro-sidebar";
import {
  LayoutDashboard, Users, AppWindow, FileText, Headphones,
  LogOut, ChartColumn, CircleUser, SquareChartGantt, ChevronDown, ChartSpline, AlertTriangle, X,
  NotepadTextDashed
} from 'lucide-react';
import { Img } from "components";
import Logo from '../../Assests/Images/fuelingoLogo.png';
function LogoutModal({ isOpen, onConfirm, onCancel }) {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onCancel}
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 animate-fadeIn"
      />

      {/* Modal */}
      <div  className="fixed inset-0 z-50 flex items-center justify-center px-4">
        <div style={{background:'white'}} className="relative w-full max-w-md bg-white rounded-2xl p-6 sm:p-8 shadow-2xl animate-slideUp">
                    <button
            onClick={onCancel}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg bg-[#FEE2E2] text-[#EA352B] hover:bg-red-100 hover:text-red-500 transition"
          >
            <X size={16} />
          </button>

          {/* Icon (CENTERED) */}
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-[#FEE2E2]  flex items-center justify-center">
              <LogOut size={26} className="text-[#EA352B]" />
            </div>
          </div>

          {/* Text */}
          <div className="text-center">
            <h2 className="mb-2 text-2xl sm:text-xl text-gray-900">
              Do you want to log out?
            </h2>

            <p className="mb-6 text-sm sm:text-base text-gray-500 leading-relaxed">
              Are you sure you want to log out? Your session will be ended.
            </p>
          </div>
          {/* <div className="h-px bg-[#FEE2E2] mb-5" /> */}
          <div className="flex sm:flex-row gap-3">
            <button
              onClick={onCancel}
              className="w-full py-3 rounded-lg border border-[#EA352B] text-[#EA352B] font-medium hover:bg-gray-50 transition"
            >
              Cancel
            </button>

            <button
            style={{backgroundColor: '#FEE2E2', color: '#EA352B'}}
              onClick={onConfirm}
              className="w-full py-3 rounded-lg text-white font-medium flex items-center justify-center gap-2 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style>
        {`
          .animate-fadeIn {
            animation: fadeIn 0.2s ease;
          }
          .animate-slideUp {
            animation: slideUp 0.25s cubic-bezier(0.34,1.56,0.64,1);
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </>
  );
}

/* ─── Main Sidebar ─── */
export default function Sidebar1() {
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
const navigate = useNavigate();
const handleLogout = () => {
  localStorage.clear();
  setShowLogoutModal(false);
  navigate("/");
};
  const menuItems = [
    { name: "Dashboard", icon: <LayoutDashboard size={18} />, path: "/admin-dashboard" },
    {
      name: "User Management",
      icon: <SquareChartGantt size={18} />,
      path: "/user-management",
      isSubMenu: true,
      children: [
        { name: "Customer", path: "/user-management/customer" },
        { name: "Service Provider", path: "/user-management/service-provider" },
      ]
    },
    { name: "Community", icon: <Users size={18} />, path: "/community" },
    {
      name: "Applications",
      icon: <AppWindow size={18} />,
      path: "/applications",
      isSubMenu: true,
      children: [
        { name: "Pending", path: "/applications/pending" },
        { name: "Approved", path: "/applications/approved" },
        { name: "Rejected", path: "/applications/rejected" },
      ]
    },
        { name: "Customer Feedbacks", icon: <NotepadTextDashed  size={18} />, path: "/admin-all-feedbacks" },
    // { name: "Reports", icon: <ChartColumn size={18} />, path: "/reports" },
    // { name: "Customer Support", icon: <CircleUser size={18} />, path: "/support" },
    { name: "Fuel Prices", icon: <ChartSpline size={18} />, path: "/admin-fuel-prices" },
  ];

  const getActiveSubMenu = () => {
    const match = menuItems.find(
      (item) => item.isSubMenu && location.pathname.startsWith(item.path)
    );
    return match ? match.name : null;
  };

  const [openSubMenu, setOpenSubMenu] = useState(getActiveSubMenu);

  useEffect(() => {
    setOpenSubMenu(getActiveSubMenu());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {/*
        FIX: react-pro-sidebar ka submenu jab controlled `open` prop se toggle hota hai
        (route change par), to uska height animation kabhi kabhi close hone ke baad
        bhi last item ("Rejected") ko visible chhod deta hai.
        Yeh CSS band submenu ko force karke hide kar deta hai.
      */}
      <style>
        {`
          .ps-submenu-root:not(.ps-open) > .ps-submenu-content {
            display: none !important;
            height: 0 !important;
            overflow: hidden !important;
            visibility: hidden !important;
          }
        `}
      </style>

      <Sidebar
        rootStyles={{
          [`.${sidebarClasses.container}`]: {
            backgroundColor: '#F8F8F8',
            width: '250px',
            borderRight: 'none',
          },
        }}
        className="h-screen sticky top-0 border-r border-gray-100 shadow-sm"
      >
        {/* Logo Section */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: '1em', marginLeft: '1.5em', paddingBottom: '1em' }}>
          <Img src={Logo} alt="Sidebar Logo" className="h-[2.40rem] object-contain" />
        </div>

        <div className="flex flex-col h-[calc(100vh-140px)] justify-between">
          <Menu
            menuItemStyles={{
              button: ({ active, level }) => ({
                backgroundColor: active && level === 0 ? '#FEE2E2' : '#F9FAFB',
                color: active ? '#EA352B' : '#000000bf',
                paddingLeft: level === 0 ? '32px' : '65px',
                margin: level === 0 ? '4px 16px' : '0px 16px',
                borderRadius: '8px',
                padding: '10px 12px',
                fontSize: '16px',
                fontWeight: 500,
                fontFamily: "'NeueMontreal', sans-serif",
                // FIX: `all` ki jagah sirf colors animate karo (layout/height glitch nahi hoga)
                transition: 'background-color 0.2s ease, color 0.2s ease',
                '&:hover': {
                  backgroundColor: active ? '#FEE2E2' : '#F9FAFB',
                  color: '#EA352B',
                },
              }),
              subMenuContent: {
                backgroundColor: 'transparent',
                paddingLeft: '20px',
              },
              SubMenuExpandIcon: {
                '& span': { width: '8px', height: '8px', borderRight: '2px solid', borderBottom: '2px solid' },
                svg: { width: '20px', height: '20px' }
              },
            }}
          >
            {menuItems.map((item) => {
              if (item.isSubMenu) {
                const isChildActive = item.children.some(child => location.pathname === child.path);
                return (
                  <SubMenu
                    style={{ gap: '10px', fontFamily: "'NeueMontreal', sans-serif" }}
                    key={item.name}
                    label={item.name}
                    icon={item.icon}
                    active={isChildActive || location.pathname.startsWith(item.path)}
                    open={openSubMenu === item.name}
                    onOpenChange={(isOpen) =>
                      setOpenSubMenu(isOpen ? item.name : null)
                    }
                  >
                    {item.children.map((child) => (
                      <MenuItem
                        style={{ gap: '10px', fontFamily: "'NeueMontreal', sans-serif" }}
                        key={child.name}
                        component={<NavLink to={child.path} />}
                        active={location.pathname === child.path}
                      >
                        {child.name}
                      </MenuItem>
                    ))}
                  </SubMenu>
                );
              }
              return (
                <MenuItem
                  style={{ gap: '10px', fontFamily: "'NeueMontreal', sans-serif" }}
                  key={item.name}
                  component={<NavLink to={item.path} />}
                  icon={item.icon}
                  active={location.pathname === item.path}
                >
                  {item.name}
                </MenuItem>
              );
            })}
          </Menu>

          {/* Logout Button */}
          <Menu
            className="mb-6"
            menuItemStyles={{
              button: {
                color: '#111827',
                paddingLeft: '32px',
                margin: '4px 16px',
                fontSize: '14px',
                fontWeight: 500,
                '&:hover': { color: '#EA352B', backgroundColor: 'transparent' }
              }
            }}
          >
            <MenuItem
              style={{ gap: '10px', fontFamily: "'NeueMontreal', sans-serif",  fontSize: '16px',paddingLeft: '28px',
                margin: '4px 0px', }}
               menuItemStyles={{
              button: {
                color: '#111827',
                fontWeight: 500,
                '&:hover': { color: '#EA352B', backgroundColor: 'transparent' }
              }
            }}
              icon={<LogOut size={18} />}
              onClick={() => setShowLogoutModal(true)}
            >
              Logout
            </MenuItem>
          </Menu>
        </div>
      </Sidebar>

      {/* Logout Confirmation Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onConfirm={handleLogout}
        onCancel={() => setShowLogoutModal(false)}
      />
    </>
  );
}